import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AREA_KIND_LABELS,
  SMS_PROVIDER_LABELS,
  US_STATES,
  coverageLabel,
  type AuditEvent,
  type Role,
  type TenantIssue,
  type User,
} from '@ww/shared';
import { api, type TenantSummary, type UserPatch } from '../api';
import AreaPicker, { type AreaValue } from '../components/AreaPicker';
import WatchAreasPanel from '../components/WatchAreasPanel';
import { ErrorBox, Loading, PageHeader, PasswordField, Toggle } from '../components/ui';
import { useLive } from '../hooks/useLive';
import { ROLE_LABELS, errorMessage } from '../lib/status';
import { fmtDateTime } from '../lib/time';

const ZONE_RE = /^[A-Z]{2}[CZ]\d{3}$/;

function Stat({ label, value, warn }: { label: string; value: number | string; warn?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <span className="cond" style={{ fontSize: 24, fontWeight: 700, color: warn ? '#ffb547' : undefined }}>
        {value}
      </span>
      <span className="muted" style={{ fontSize: 12.5 }}>
        {label}
      </span>
    </div>
  );
}

export default function TenantDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setActingTenantId, refreshTenants } = useLive();
  const justCreated = (location.state as { justCreated?: boolean } | null)?.justCreated ?? false;

  const [s, setS] = useState<TenantSummary | null>(null);
  const [users, setUsers] = useState<User[] | null>(null);
  const [audit, setAudit] = useState<AuditEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [editingArea, setEditingArea] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [draft, setDraft] = useState({ name: '', countyName: '', state: '', lat: '', lon: '', timezone: '' });
  const [confirmSlug, setConfirmSlug] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [nu, setNu] = useState({ name: '', username: '', email: '', role: 'viewer' as Role, password: '' });
  const [resetting, setResetting] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [areaValue, setAreaValue] = useState<AreaValue>({ area: null, boundary: null, center: null });
  const [zoneInput, setZoneInput] = useState('');

  const load = useCallback(async () => {
    try {
      const summary = await api.tenantSummary(id);
      setS(summary);
      setAreaValue({
        area: summary.tenant.area,
        boundary: summary.tenant.boundary,
        center: summary.tenant.center,
      });
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
    try {
      setUsers(await api.users(id));
    } catch {
      setUsers([]);
    }
    try {
      setAudit(await api.auditFor(id, 12));
    } catch {
      setAudit([]);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!s && !error) return <Loading label="Loading tenant…" />;
  if (!s) return <ErrorBox error={error} />;

  const t = s.tenant;
  const blockers = s.issues.filter((i) => i.severity === 'error');

  const patch = async (body: Parameters<typeof api.updateTenant>[1], msg: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.updateTenant(id, body);
      setNotice(msg);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const reDerive = async () => {
    if (!t.area) return setError('This tenant has no coverage area to derive from.');
    setBusy(true);
    setError(null);
    try {
      const dv = await api.deriveZones({
        state: t.state,
        kind: t.area.kind,
        name: t.area.label,
        lon: t.center[0],
        lat: t.center[1],
      });
      const added = dv.zoneCodes.filter((z) => !t.zoneCodes.includes(z));
      const removed = t.zoneCodes.filter((z) => !dv.zoneCodes.includes(z));
      await api.updateTenant(id, { zoneCodes: dv.zoneCodes });
      setNotice(
        `Zone codes re-derived${added.length ? `, added ${added.join(', ')}` : ''}${
          removed.length ? `, removed ${removed.join(', ')}` : ''
        }.`,
      );
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const saveUser = async (u: User, patchBody: UserPatch, msg: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.updateUser(u._id, patchBody);
      setNotice(msg);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const removeUser = async (u: User) => {
    // Removing the last admin would lock the customer out of their own tenant.
    const admins = (users ?? []).filter((x) => x.role !== 'viewer' && x.status === 'active');
    if (u.role !== 'viewer' && admins.length <= 1) {
      setError(`${u.username} is the only active admin for this tenant. Add another before removing them.`);
      return;
    }
    if (!window.confirm(`Remove ${u.username} from ${t.name}? This cannot be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteUser(u._id);
      setNotice(`Removed ${u.username}.`);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const addUser = async () => {
    if (!nu.name.trim() || !nu.username.trim() || !nu.email.trim() || nu.password.length < 8) {
      setError('A new user needs a name, username, email and a password of at least 8 characters.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createUser({
        tenantId: t._id,
        name: nu.name.trim(),
        username: nu.username.trim(),
        email: nu.email.trim(),
        role: nu.role,
        password: nu.password,
      });
      setNotice(`Created ${nu.username}.`);
      setNu({ name: '', username: '', email: '', role: 'viewer', password: '' });
      setAddingUser(false);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const viewAsTenant = () => {
    setActingTenantId(t._id);
    navigate('/');
  };

  return (
    <div className="page page-scroll" style={{ gap: 14 }}>
      <PageHeader title={t.name} sub={`${t.slug} · ${t.state} · watching ${coverageLabel(t)}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Toggle
            on={t.status === 'active'}
            label={`Tenant ${t.name} active`}
            onChange={() => void patch({ status: t.status === 'active' ? 'disabled' : 'active' }, 'Status updated.')}
          />
          <button type="button" className="btn btn-secondary btn-sm hov" onClick={() => navigate('/admin')}>
            All tenants
          </button>
          <button type="button" className="btn btn-primary btn-sm hov" onClick={viewAsTenant}>
            Drop in to their portal
          </button>
        </div>
      </PageHeader>

      <ErrorBox error={error} />
      {notice && <div className="ok-box">{notice}</div>}
      {justCreated && <div className="ok-box">Tenant created.</div>}

      {s.issues.length > 0 && (
        <div className={blockers.length ? 'err-box' : 'warn-box'}>
          <strong>{blockers.length ? 'This tenant is not fully live' : 'Worth finishing'}</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {s.issues.map((i: TenantIssue) => (
              <li key={i.code}>{i.message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="panel" style={{ padding: 16, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        <Stat label="Users" value={s.counts.users} warn={s.counts.users === 0} />
        <Stat label="Assets" value={`${s.counts.enabledAssets}/${s.counts.assets}`} />
        <Stat label="Notification groups" value={s.counts.notificationGroups} warn={s.counts.notificationGroups === 0} />
        <Stat label="SMS recipients" value={s.counts.recipients} warn={s.counts.recipients === 0} />
        <Stat label="Active alerts" value={s.counts.activeAlerts} />
        <Stat label="Notifications (24h)" value={s.counts.notifications24h} />
        <Stat label="Failed (24h)" value={s.counts.failedNotifications24h} warn={s.counts.failedNotifications24h > 0} />
      </div>

      <div className="detail-grid">
        <section className="panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span className="lbl" style={{ flexGrow: 1 }}>
              Coverage area
            </span>
            <button type="button" className="btn-link hov" onClick={() => setEditingArea((v) => !v)}>
              {editingArea ? 'Cancel' : 'Change'}
            </button>
          </div>
          <dl className="kv">
            <dt>Area</dt>
            <dd>
              <span className={`chip area-chip area-${t.area?.kind ?? 'county'}`}>
                {t.area ? AREA_KIND_LABELS[t.area.kind] : 'County'}
              </span>{' '}
              {coverageLabel(t)}
            </dd>
            <dt>Centre</dt>
            <dd className="mono">
              {t.center[1].toFixed(4)}, {t.center[0].toFixed(4)}
            </dd>
            <dt>Timezone</dt>
            <dd className="mono">{t.timezone}</dd>
          </dl>
          {editingArea && (
            <>
              <AreaPicker
                state={t.state}
                value={areaValue}
                onChange={setAreaValue}
                tenant={{ center: t.center, boundary: t.boundary, timezone: t.timezone }}
                mapHeight={300}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm hov"
                style={{ alignSelf: 'flex-end' }}
                disabled={busy || !areaValue.boundary || !areaValue.area}
                onClick={() =>
                  void patch(
                    { boundary: areaValue.boundary, area: areaValue.area },
                    'Coverage area updated. Re-derive the zone codes if the county changed.',
                  ).then(() => setEditingArea(false))
                }
              >
                Save coverage area
              </button>
            </>
          )}
        </section>

        <WatchAreasPanel
          state={t.state}
          tenantId={t._id}
          allowCounty
          readOnly={false}
          coverageIsCounty={(t.area?.kind ?? 'county') === 'county'}
        />

        <section className="panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span className="lbl">NWS zone codes</span>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            Alerts issued without a polygon — most Watches, and many advisories — are matched by
            these codes alone.
          </p>
          <div className="zone-chips">
            {t.zoneCodes.map((z) => (
              <span key={z} className="chip mono">
                {z}
                <button
                  type="button"
                  className="btn-link hov"
                  aria-label={`Remove ${z}`}
                  disabled={busy}
                  onClick={() =>
                    void patch({ zoneCodes: t.zoneCodes.filter((x) => x !== z) }, 'Zone codes updated.')
                  }
                >
                  ×
                </button>
              </span>
            ))}
            {!t.zoneCodes.length && <span className="muted">none</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="fld mono"
              placeholder="ARC085"
              value={zoneInput}
              onChange={(e) => setZoneInput(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm hov"
              disabled={busy}
              onClick={() => {
                const next = zoneInput.trim().toUpperCase();
                if (!ZONE_RE.test(next)) return setError('Zone codes look like ARC085 or ARZ045.');
                setZoneInput('');
                void patch({ zoneCodes: Array.from(new Set([...t.zoneCodes, next])).sort() }, 'Zone codes updated.');
              }}
            >
              Add
            </button>
            <button type="button" className="btn btn-secondary btn-sm hov" disabled={busy} onClick={() => void reDerive()}>
              Re-derive from coverage
            </button>
          </div>
        </section>

        <section className="panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span className="lbl" style={{ flexGrow: 1 }}>
              Users
            </span>
            <button type="button" className="btn-link hov" onClick={() => setAddingUser((v) => !v)}>
              {addingUser ? 'Cancel' : 'Add user'}
            </button>
          </div>
          {!users && <span className="muted">Loading...</span>}
          {users && users.length === 0 && (
            <span className="muted">No users - nobody can sign in to this tenant yet.</span>
          )}
          {users && users.length > 0 && (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Active</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u._id}>
                    <td>{u.name}</td>
                    <td className="mono">{u.username}</td>
                    <td>
                      <select
                        className="fld"
                        style={{ height: 30, fontSize: 13 }}
                        value={u.role}
                        disabled={busy || u.role === 'super_admin'}
                        onChange={(e) => void saveUser(u, { role: e.target.value as Role }, 'Role updated.')}
                      >
                        <option value="viewer">{ROLE_LABELS.viewer}</option>
                        <option value="tenant_admin">{ROLE_LABELS.tenant_admin}</option>
                      </select>
                    </td>
                    <td>
                      <Toggle
                        on={u.status === 'active'}
                        label={`${u.username} active`}
                        disabled={busy}
                        onChange={() =>
                          void saveUser(u, { status: u.status === 'active' ? 'disabled' : 'active' }, 'User updated.')
                        }
                      />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn-link hov"
                        disabled={busy}
                        onClick={() => {
                          setNewPassword('');
                          setResetting(resetting === u._id ? null : u._id);
                        }}
                      >
                        Password
                      </button>
                      <button
                        type="button"
                        className="btn-link hov"
                        style={{ color: 'var(--err-text)', marginLeft: 10 }}
                        disabled={busy}
                        onClick={() => void removeUser(u)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {resetting && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <PasswordField
                label={`New password for ${users?.find((u) => u._id === resetting)?.username ?? ''}`}
                autoComplete="new-password"
                value={newPassword}
                onChange={setNewPassword}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm hov"
                disabled={busy || newPassword.length < 8}
                onClick={() => {
                  const u = users?.find((x) => x._id === resetting);
                  if (u) void saveUser(u, { password: newPassword }, `Password reset for ${u.username}.`);
                  setNewPassword('');
                  setResetting(null);
                }}
              >
                Set password
              </button>
            </div>
          )}

          {addingUser && (
            <div className="grid-2" style={{ alignItems: 'end' }}>
              <label className="field">
                <span className="lbl">Full name</span>
                <input className="fld" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} />
              </label>
              <label className="field">
                <span className="lbl">Username</span>
                <input
                  className="fld mono"
                  value={nu.username}
                  onChange={(e) => setNu({ ...nu, username: e.target.value })}
                />
              </label>
              <label className="field">
                <span className="lbl">Email</span>
                <input
                  className="fld"
                  type="email"
                  value={nu.email}
                  onChange={(e) => setNu({ ...nu, email: e.target.value })}
                />
              </label>
              <label className="field">
                <span className="lbl">Role</span>
                <select
                  className="fld"
                  value={nu.role}
                  onChange={(e) => setNu({ ...nu, role: e.target.value as Role })}
                >
                  <option value="viewer">{ROLE_LABELS.viewer}</option>
                  <option value="tenant_admin">{ROLE_LABELS.tenant_admin}</option>
                </select>
              </label>
              <PasswordField
                label="Initial password"
                autoComplete="new-password"
                value={nu.password}
                onChange={(v) => setNu({ ...nu, password: v })}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm hov"
                disabled={busy}
                onClick={() => void addUser()}
                style={{ marginBottom: 2 }}
              >
                Create user
              </button>
            </div>
          )}
        </section>

        <section className="panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span className="lbl">Notifications</span>
          {s.counts.recipients === 0 ? (
            <div className="warn-box">
              No SMS recipients are configured. Warnings for this tenant will be recorded as failed
              rather than delivered.
            </div>
          ) : (
            <dl className="kv">
              <dt>Groups</dt>
              <dd>{s.counts.notificationGroups}</dd>
              <dt>Recipients</dt>
              <dd>{s.counts.recipients}</dd>
            </dl>
          )}
          {/* Which gateway this tenant sends through, and whether its own key is installed.
              The key itself never leaves the server — only the masked hint. */}
          {s.sms && (
            <dl className="kv">
              <dt>SMS gateway</dt>
              <dd>{SMS_PROVIDER_LABELS[s.sms.provider] ?? s.sms.provider}</dd>
              <dt>API key</dt>
              <dd style={{ color: s.sms.configured ? undefined : '#ffc36b' }}>
                {s.sms.configured ? s.sms.keyHint : 'not installed'}
              </dd>
              {s.sms.lastTestAt && (
                <>
                  <dt>Last test</dt>
                  <dd style={{ color: s.sms.lastTestResult === 'failed' ? '#ff8f93' : undefined }}>
                    {s.sms.lastTestResult} · {new Date(s.sms.lastTestAt).toLocaleString()}
                  </dd>
                </>
              )}
            </dl>
          )}
          <button
            type="button"
            className="btn-link hov"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => {
              setActingTenantId(t._id);
              navigate('/rules');
            }}
          >
            Edit groups in this tenant’s Alert rules →
          </button>
        </section>

        <section className="panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span className="lbl" style={{ flexGrow: 1 }}>
              Details
            </span>
            <button
              type="button"
              className="btn-link hov"
              onClick={() => {
                setDraft({
                  name: t.name,
                  countyName: t.countyName,
                  state: t.state,
                  lat: String(t.center[1]),
                  lon: String(t.center[0]),
                  timezone: t.timezone,
                });
                setEditingDetails((v) => !v);
              }}
            >
              {editingDetails ? 'Cancel' : 'Edit'}
            </button>
          </div>
          {!editingDetails ? (
            <dl className="kv">
              <dt>Name</dt>
              <dd>{t.name}</dd>
              <dt>County name</dt>
              <dd>{t.countyName}</dd>
              <dt>State</dt>
              <dd>{t.state}</dd>
              <dt>Slug</dt>
              <dd className="mono muted">{t.slug} · fixed</dd>
            </dl>
          ) : (
            <>
              <label className="field">
                <span className="lbl">Name</span>
                <input className="fld" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </label>
              <div className="grid-2">
                <label className="field">
                  <span className="lbl">County name</span>
                  <input
                    className="fld"
                    value={draft.countyName}
                    onChange={(e) => setDraft({ ...draft, countyName: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span className="lbl">State</span>
                  <select className="fld" value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value })}>
                    {US_STATES.map((st) => (
                      <option key={st.code} value={st.code}>
                        {st.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="grid-2">
                <label className="field">
                  <span className="lbl">Centre latitude</span>
                  <input className="fld mono" value={draft.lat} onChange={(e) => setDraft({ ...draft, lat: e.target.value })} />
                </label>
                <label className="field">
                  <span className="lbl">Centre longitude</span>
                  <input className="fld mono" value={draft.lon} onChange={(e) => setDraft({ ...draft, lon: e.target.value })} />
                </label>
              </div>
              <label className="field">
                <span className="lbl">Timezone</span>
                <input
                  className="fld mono"
                  value={draft.timezone}
                  onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
                />
              </label>
              <button
                type="button"
                className="btn btn-primary btn-sm hov"
                style={{ alignSelf: 'flex-end' }}
                disabled={busy}
                onClick={() => {
                  const lat = Number(draft.lat);
                  const lon = Number(draft.lon);
                  if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180)
                    return setError('Enter a valid map centre.');
                  void patch(
                    {
                      name: draft.name.trim(),
                      countyName: draft.countyName.trim(),
                      state: draft.state,
                      center: [lon, lat],
                      timezone: draft.timezone.trim(),
                    },
                    'Details updated.',
                  ).then(() => setEditingDetails(false));
                }}
              >
                Save details
              </button>
            </>
          )}
        </section>

        <section className="panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span className="lbl">Recent activity</span>
          {!audit?.length && <span className="muted">Nothing yet.</span>}
          {audit?.map((a) => (
            <div key={a._id} style={{ fontSize: 13 }}>
              <span className="muted mono" style={{ fontSize: 12 }}>
                {fmtDateTime(a.timestamp, t.timezone)}
              </span>
              <br />
              {a.summary}
            </div>
          ))}
        </section>
      </div>

      <section className="panel danger-zone">
        <span className="lbl">Delete tenant</span>
        <p className="muted" style={{ fontSize: 13, margin: 0, maxWidth: '70ch' }}>
          Permanently removes {t.name} along with its {s.counts.users} user
          {s.counts.users === 1 ? '' : 's'}, {s.counts.assets} asset{s.counts.assets === 1 ? '' : 's'} and its alert
          and notification history. The audit trail is kept. This cannot be undone — to stop alerting
          without losing anything, switch the tenant to inactive instead.
        </p>
        {!confirmingDelete ? (
          <button
            type="button"
            className="btn btn-danger hov"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => {
              setConfirmSlug('');
              setConfirmingDelete(true);
            }}
          >
            Delete this tenant…
          </button>
        ) : (
          <>
            <label className="field" style={{ maxWidth: 360 }}>
              <span className="lbl">
                Type <code className="mono">{t.slug}</code> to confirm
              </span>
              <input
                className="fld mono"
                value={confirmSlug}
                autoComplete="off"
                onChange={(e) => setConfirmSlug(e.target.value)}
              />
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn btn-ghost hov" onClick={() => setConfirmingDelete(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger-solid hov"
                disabled={busy || confirmSlug !== t.slug}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await api.deleteTenant(t._id, confirmSlug);
                    // Stop acting as a tenant that no longer exists.
                    setActingTenantId(null);
                    await refreshTenants();
                    navigate('/admin');
                  } catch (err) {
                    setError(errorMessage(err));
                    setBusy(false);
                  }
                }}
              >
                Delete {t.name} permanently
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
